import { apiService } from '@/services/api'
import type { SparklesHistory } from '@/types'
import { createIcons, Sparkles } from 'lucide'

export class SparklesManager {
  private userId: string
  private sparklesBalance: number = 0
  private isLoading: boolean = false

  private static readonly SPARKLES_PER_STAR = 10
  private static readonly MIN_STAR_PURCHASE = 1
  private static readonly MAX_STAR_PURCHASE = 1000
  
  static readonly CONVERSION_RATE = 10 // 1 star = 10 sparkles

  constructor(userId: string) {
    this.userId = userId
  }

  async loadSparklesBalance(): Promise<void> {
    try {
      const response = await apiService.getSparklesBalance(this.userId)
      this.sparklesBalance = response.sparkles_balance
      this.updateBalanceDisplay()
    } catch (error) {
      console.error('Failed to load sparkles balance:', error)
      this.sparklesBalance = 0
      this.updateBalanceDisplay()
    }
  }

  getSparklesBalance(): number {
    return this.sparklesBalance
  }



  static calculateSparklesFromStars(stars: number): number {
    return stars * SparklesManager.SPARKLES_PER_STAR
  }

  static formatSparkles(amount: number): string {
    return amount.toString()
  }

  async createSparklesInvoice(starsAmount: number): Promise<string> {
    if (starsAmount < SparklesManager.MIN_STAR_PURCHASE) {
      throw new Error(`Minimum purchase is ${SparklesManager.MIN_STAR_PURCHASE} star`)
    }

    if (starsAmount > SparklesManager.MAX_STAR_PURCHASE) {
      throw new Error(`Maximum purchase is ${SparklesManager.MAX_STAR_PURCHASE} stars`)
    }

    if (!Number.isInteger(starsAmount) || starsAmount <= 0) {
      throw new Error('Stars amount must be a positive integer')
    }

    this.isLoading = true
    try {
      const response = await apiService.createSparklesInvoice(this.userId, starsAmount)
      return response.invoice_link
    } finally {
      this.isLoading = false
    }
  }

  async getSparklesHistory(limit = 20, offset = 0): Promise<SparklesHistory> {
    return await apiService.getSparklesHistory(this.userId, limit, offset)
  }

  async refreshBalance(): Promise<void> {
    try {
      const balance = await apiService.getSparklesBalance(this.userId)
      this.sparklesBalance = balance.sparkles_balance
      
      // Update the UI display
      this.updateBalanceDisplay()
      
      // Dispatch event to update UI
      window.dispatchEvent(new CustomEvent('sparklesBalanceChanged', {
        detail: { 
          sparklesBalance: this.sparklesBalance
        }
      }))
    } catch (error) {
      console.error('Failed to refresh sparkles balance:', error)
    }
  }

  updateBalanceDisplay(): void {
    const container = document.getElementById('sparklesBalanceContainer')
    if (container) {
      container.innerHTML = this.renderSparklesBalance()
      // Initialize Lucide icons after adding HTML
      createIcons({ icons: { Sparkles } })
    }
  }

  isLoadingPurchase(): boolean {
    return this.isLoading
  }

  renderSparklesBalance(): string {
    return `
      <div class="sparkles-balance-display">
        <div class="balance-item sparkles">
          <i data-lucide="sparkles" class="balance-icon"></i>
          <span class="balance-amount">${SparklesManager.formatSparkles(this.sparklesBalance)}</span>
        </div>
      </div>
    `
  }

  renderSparklesPurchaseModal(): string {
    const maxPurchase = SparklesManager.MAX_STAR_PURCHASE
    return `
      <div id="sparklesPurchaseModal" class="modal-backdrop" style="display: none;">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Purchase Sparkles</h3>
            <button class="close-btn" onclick="sparklesManager.hidePurchaseModal()">&times;</button>
          </div>
          <div class="modal-body">
            <div class="purchase-info">
              <p>Convert your Telegram Stars to Sparkles for marketplace purchases!</p>
              <div class="conversion-rate">
                <span class="rate-text">1 ★ = ${SparklesManager.SPARKLES_PER_STAR} <i data-lucide="sparkles"></i></span>
              </div>
            </div>
            
            <div class="balance-display">
              <div class="balance-item">
                <span class="balance-label">Your Sparkles:</span>
                <span class="balance-amount">${SparklesManager.formatSparkles(this.sparklesBalance)}<i data-lucide="sparkles"></i></span>
              </div>
            </div>

            <div class="purchase-form">
              <label for="starsAmountInput">Stars to convert:</label>
              <input 
                type="number" 
                id="starsAmountInput" 
                min="${SparklesManager.MIN_STAR_PURCHASE}" 
                max="${maxPurchase}"
                step="1"
                placeholder="Enter amount..."

              >
              

              <div class="conversion-preview" id="conversionPreview" style="display: none;">
                <span class="preview-text">You will receive: <strong id="sparklesPreview">0</strong> <i data-lucide="sparkles"></i></span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn secondary" onclick="sparklesManager.hidePurchaseModal()">Cancel</button>
            <button 
              class="btn danger" 
              onclick="sparklesManager.confirmPurchase()"
              id="confirmPurchaseBtn"
            >
              Purchase Sparkles
            </button>
          </div>
        </div>
      </div>
    `
  }

  showPurchaseModal(): void {
    const existingModal = document.getElementById('sparklesPurchaseModal')
    if (existingModal) {
      existingModal.remove()
    }

    document.body.insertAdjacentHTML('beforeend', this.renderSparklesPurchaseModal())
    
    // Initialize Lucide icons after adding HTML
    createIcons({ icons: { Sparkles } })
    
    const modal = document.getElementById('sparklesPurchaseModal')
    const input = document.getElementById('starsAmountInput') as HTMLInputElement
    
    if (modal && input) {
      modal.style.display = 'flex'
      document.body.style.overflow = 'hidden'
      
      // Add input event listener for live preview
      input.addEventListener('input', () => this.updateConversionPreview())
      
      // Focus the input
      setTimeout(() => input.focus(), 100)
    }
  }

  hidePurchaseModal(): void {
    const modal = document.getElementById('sparklesPurchaseModal')
    if (modal) {
      modal.style.display = 'none'
      document.body.style.overflow = ''
      modal.remove()
    }
  }

  setStarsAmount(amount: number): void {
    const input = document.getElementById('starsAmountInput') as HTMLInputElement
    if (input) {
      input.value = amount.toString()
      this.updateConversionPreview()
    }
  }

  private updateConversionPreview(): void {
    const input = document.getElementById('starsAmountInput') as HTMLInputElement
    const preview = document.getElementById('conversionPreview')
    const sparklesPreview = document.getElementById('sparklesPreview')
    const confirmBtn = document.getElementById('confirmPurchaseBtn') as HTMLButtonElement

    if (!input || !preview || !sparklesPreview || !confirmBtn) return

    const starsAmount = Number(input.value)
    const wasPreviewVisible = preview.style.display === 'block'
    
    if (starsAmount > 0 && Number.isInteger(starsAmount)) {
      const sparklesAmount = SparklesManager.calculateSparklesFromStars(starsAmount)
      sparklesPreview.textContent = SparklesManager.formatSparkles(sparklesAmount)
      preview.style.display = 'block'
      
      // Scroll to bottom when preview becomes visible
      if (!wasPreviewVisible) {
        this.scrollModalToBottom()
      }
      
      const isValid = starsAmount >= SparklesManager.MIN_STAR_PURCHASE && 
                     starsAmount <= SparklesManager.MAX_STAR_PURCHASE
      
      confirmBtn.disabled = !isValid || this.isLoading
      confirmBtn.textContent = this.isLoading ? 'Processing...' : 'Purchase Sparkles'
    } else {
      preview.style.display = 'none'
      confirmBtn.disabled = true
      confirmBtn.textContent = 'Purchase Sparkles'
    }
  }

  private scrollModalToBottom(): void {
    const modal = document.getElementById('sparklesPurchaseModal')
    if (modal) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        const modalContent = modal.querySelector('.modal-content') as HTMLElement
        if (modalContent) {
          modalContent.scrollTo({
            top: modalContent.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 50)
    }
  }

  async confirmPurchase(): Promise<void> {
    const input = document.getElementById('starsAmountInput') as HTMLInputElement
    if (!input) return

    const starsAmount = Number(input.value)
    
    if (!Number.isInteger(starsAmount) || starsAmount <= 0) {
      this.showError('Please enter a valid amount of stars.')
      return
    }

    if (starsAmount < SparklesManager.MIN_STAR_PURCHASE) {
      this.showError(`Minimum purchase is ${SparklesManager.MIN_STAR_PURCHASE} star.`)
      return
    }

    // Note: Stars balance validation is handled by Telegram invoice system

    try {
      const invoiceLink = await this.createSparklesInvoice(starsAmount)
      const sparklesReceived = SparklesManager.formatSparkles(starsAmount * SparklesManager.CONVERSION_RATE)
      
      this.hidePurchaseModal()
      
      // Open the Telegram invoice
      if (window.Telegram?.WebApp?.openInvoice) {
        window.Telegram.WebApp.openInvoice(invoiceLink, (status: string) => {
          if (status === 'paid') {
            this.showSuccess(`Successfully purchased ${sparklesReceived} sparkles for ${starsAmount}★!`)
            // Refresh balance after successful payment
            this.refreshBalance()
          } else if (status === 'cancelled') {
            this.showError('Payment was cancelled')
          } else if (status === 'failed') {
            this.showError('Payment failed')
          }
        })
      } else {
        // Fallback for non-Telegram environments
        window.open(invoiceLink, '_blank')
        this.showSuccess(`Invoice created! You will receive ${sparklesReceived} sparkles for ${starsAmount}★ after payment.`)
      }
      
    } catch (error) {
      this.showError(`Failed to create invoice: ${(error as Error).message}`)
    }
  }

  private showError(message: string): void {
    // You can integrate this with your existing error display system
    console.error('Sparkles Error:', message)
    alert(message) // Temporary - replace with your UI error system
  }

  private showSuccess(message: string): void {
    // You can integrate this with your existing success display system
    console.log('Sparkles Success:', message)
    alert(message) // Temporary - replace with your UI success system
  }
}

// Global instance for modal interactions
declare global {
  interface Window {
    sparklesManager?: SparklesManager
  }
}